// =====================================================================
// EGL v12 Frontend - TOKEN MANAGEMENT UTILITY
// Purpose: Handle JWT token expiry, refresh, and session management
// =====================================================================

interface TokenInfo {
  token: string;
  expiresAt: number;
  isExpired: boolean;
  timeUntilExpiry: number;
}

interface RefreshResult {
  success: boolean;
  token?: string;
  message?: string;
  shouldLogout?: boolean;
}

export class TokenManager {
  private static readonly TOKEN_KEY = 'egl_token'; // Match SmartRouter token key
  private static readonly REFRESH_THRESHOLD = 2 * 60 * 1000; // 2 minutes before expiry
  private static readonly TOKEN_EXPIRY_MINUTES = 15;
  
  /**
   * Decode JWT token and extract expiry information
   */
  static getTokenInfo(token?: string): TokenInfo | null {
    const authToken = token || localStorage.getItem(this.TOKEN_KEY);
    
    if (!authToken) {
      console.log('🔍 TokenManager: No token found in localStorage');
      return null;
    }

    try {
      // Decode JWT payload (base64)
      const payload = JSON.parse(atob(authToken.split('.')[1]));
      const expiresAt = payload.exp * 1000; // Convert to milliseconds
      const now = Date.now();
      const timeUntilExpiry = expiresAt - now;
      const isExpired = timeUntilExpiry <= 0;

      const tokenInfo = {
        token: authToken,
        expiresAt,
        isExpired,
        timeUntilExpiry
      };

      console.log('🔍 TokenManager info:', {
        exp: payload.exp,
        expiresAt: new Date(expiresAt).toLocaleTimeString(),
        now: new Date(now).toLocaleTimeString(),
        timeUntilExpiryMs: timeUntilExpiry,
        timeUntilExpiryMin: Math.round(timeUntilExpiry / 60000),
        isExpired: tokenInfo.isExpired
      });

      return tokenInfo;
    } catch (error) {
      console.error('Failed to decode token:', error);
      return null;
    }
  }

  /**
   * Check if token needs refresh (within 2 minutes of expiry)
   */
  static needsRefresh(token?: string): boolean {
    const tokenInfo = this.getTokenInfo(token);
    if (!tokenInfo) return false;
    
    return tokenInfo.timeUntilExpiry <= this.REFRESH_THRESHOLD && !tokenInfo.isExpired;
  }

  /**
   * Check if token is expired
   */
  static isExpired(token?: string): boolean {
    const tokenInfo = this.getTokenInfo(token);
    return tokenInfo ? tokenInfo.isExpired : true;
  }

  /**
   * Get time until token expiry in human readable format
   */
  static getTimeUntilExpiry(token?: string): string {
    const tokenInfo = this.getTokenInfo(token);
    if (!tokenInfo) return 'Unknown';
    
    if (tokenInfo.isExpired) return 'Expired';
    
    const minutes = Math.floor(tokenInfo.timeUntilExpiry / (60 * 1000));
    const seconds = Math.floor((tokenInfo.timeUntilExpiry % (60 * 1000)) / 1000);
    
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  }

  /**
   * Attempt to refresh the token by re-authenticating
   * Note: This requires the user to log in again as we don't have refresh tokens
   */
  static async attemptTokenRefresh(): Promise<RefreshResult> {
    // Since we don't have refresh tokens, we need to redirect to login
    // This is a placeholder for future refresh token implementation
    return {
      success: false,
      message: 'Session expired. Please log in again.',
      shouldLogout: true
    };
  }

  /**
   * Check token before making API calls and handle expiry
   */
  static async validateTokenForApiCall(): Promise<{ valid: boolean; token?: string; message?: string }> {
    const tokenInfo = this.getTokenInfo();
    
    if (!tokenInfo) {
      return {
        valid: false,
        message: 'No authentication token found. Please log in.'
      };
    }

    if (tokenInfo.isExpired) {
      return {
        valid: false,
        message: 'Your session has expired. Please log in again.'
      };
    }

    if (this.needsRefresh(tokenInfo.token)) {
      console.warn(`Token expires in ${this.getTimeUntilExpiry(tokenInfo.token)}. Consider refreshing session.`);
    }

    return {
      valid: true,
      token: tokenInfo.token
    };
  }

  /**
   * Set up automatic token monitoring
   */
  static startTokenMonitoring(onExpiry: () => void, onNearExpiry: (timeLeft: string) => void): () => void {
    const checkInterval = setInterval(() => {
      const tokenInfo = this.getTokenInfo();
      
      if (!tokenInfo) {
        onExpiry();
        return;
      }

      if (tokenInfo.isExpired) {
        onExpiry();
        return;
      }

      if (this.needsRefresh(tokenInfo.token)) {
        onNearExpiry(this.getTimeUntilExpiry(tokenInfo.token));
      }
    }, 30000); // Check every 30 seconds

    // Return cleanup function
    return () => clearInterval(checkInterval);
  }

  /**
   * Clear token and related data
   */
  static clearToken(): void {
    localStorage.removeItem(this.TOKEN_KEY);
  }

  /**
   * Store new token
   */
  static setToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  /**
   * Get stored token
   */
  static getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }
} 